import type { AccountId } from "@repo/app-types/entities/account";
import type { DateRange } from "@repo/app-types/entities/filters";
import type { GroupId } from "@repo/app-types/entities/group";
import type {
  CustomerCallActivity,
  PerformanceCustomerDetail,
  PerformanceCustomerRow,
} from "@repo/app-types/entities/performanceCustomer";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { buildListTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";
import { buildTeammatePerformanceGradingCacheTag } from "./performancePlaybook.js";

interface CustomerListQueryArgs {
  search: string;
  teammateGroupIds: readonly GroupId[] | null;
  dateRange: DateRange;
  teammateId: TeammateId | null;
}

const customerApi = api.injectEndpoints({
  endpoints: (builder) => ({
    customers: builder.infiniteQuery<
      {
        customers: readonly PerformanceCustomerRow[];
        nextCursor: string | null;
      },
      CustomerListQueryArgs,
      string | null,
      {
        data: { customers: readonly PerformanceCustomerRow[] };
        next_cursor: string | null;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "POST",
        url: "/performance/customers",
        body: {
          search: queryArg.search,
          teammateGroupIds: queryArg.teammateGroupIds,
          teammateId: queryArg.teammateId,
          dateRange: queryArg.dateRange,
          cursor: pageParam,
        },
      }),
      transformResponse: (response) => ({
        customers: response.data.customers,
        nextCursor: response.next_cursor,
      }),
    }),
    customerDetail: builder.query<
      PerformanceCustomerDetail,
      AccountId,
      { data: PerformanceCustomerDetail }
    >({
      query: (accountId) => ({
        method: "GET",
        url: `/performance/customers/${accountId}`,
      }),
      transformResponse: (response) => response.data,
      // Grade edits (reassess, validation) from the call slideover refetch the
      // scores. Split rows show no assessment, so only unsplit gradings count.
      providesTags: (result) => {
        if (!result) {
          return [];
        }

        const listTag = buildListTag(TagType.TeammatePerformanceGrading, []);
        const entityTags = result.playbook_scores
          .map((score) => score.grading_id)
          .filter((gradingId) => gradingId !== null)
          .map(buildTeammatePerformanceGradingCacheTag);
        return [listTag, ...entityTags];
      },
    }),
    customerTimeline: builder.infiniteQuery<
      { calls: readonly CustomerCallActivity[]; nextCursor: string | null },
      AccountId,
      string | null,
      {
        data: { calls: readonly CustomerCallActivity[] };
        next_cursor: string | null;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg: accountId, pageParam }) => ({
        method: "GET",
        url: `/performance/customers/${accountId}/timeline`,
        params: { cursor: pageParam ?? undefined },
      }),
      transformResponse: (response) => ({
        calls: response.data.calls,
        nextCursor: response.next_cursor,
      }),
      // Grade edits refetch split calls' summaries.
      providesTags: (result) => {
        if (!result) {
          return [];
        }

        const listTag = buildListTag(TagType.TeammatePerformanceGrading, []);
        const entityTags = result.pages
          .flatMap((page) => page.calls)
          .map((call) =>
            buildTeammatePerformanceGradingCacheTag(call.grading_id),
          );
        return [listTag, ...entityTags];
      },
    }),
  }),
});

export const {
  useCustomersInfiniteQuery,
  useCustomerDetailQuery,
  useCustomerTimelineInfiniteQuery,
} = customerApi;
